import React, { useState } from "react";
import { Check, X, ChevronDown } from "lucide-react";
import { getInitials } from "../lib/utils";

function StatusDot({ implemented, onClick, active, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1 p-1 transition-all ${
        active ? "bg-neutral-100" : "hover:bg-neutral-50"
      }`}
      aria-label={`${label} · ${implemented ? "Implementado" : "Pendiente"}`}
    >
      <span
        className={`w-5 h-5 grid place-items-center shrink-0 ${
          implemented ? "bg-emerald-500 rounded-full" : "bg-red-500"
        } ${active ? "ring-2 ring-neutral-950 ring-offset-1" : ""}`}
      >
        {implemented ? (
          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
        ) : (
          <X className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
        )}
      </span>
      <span className="text-[9px] leading-tight text-center text-neutral-700 line-clamp-2 w-full">
        {label}
      </span>
    </button>
  );
}

function PlantCard({
  company,
  modules,
  implementations,
  commitments,
  defaultCommitment,
  showLeader = true,
  progress = {},
}) {
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const total = modules.length;
  const done = modules.filter((m) => implementations[company.id]?.[m.id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const pctColor =
    pct >= 80
      ? "text-emerald-600"
      : pct >= 50
      ? "text-amber-500"
      : "text-red-600";

  const selectedModule = modules.find((m) => m.id === selectedModuleId);
  const selectedImpl = selectedModule
    ? Boolean(implementations[company.id]?.[selectedModule.id])
    : null;
  const selectedCommit =
    selectedModule && !selectedImpl
      ? commitments[company.id]?.[selectedModule.id] || defaultCommitment
      : null;

  // Mostrar 2 renglones (8 módulos) por defecto
  const COLLAPSED_COUNT = 8;
  const visibleModules = expanded ? modules : modules.slice(0, COLLAPSED_COUNT);
  const hasMore = modules.length > COLLAPSED_COUNT;

  return (
    <div
      className="border border-neutral-200 bg-white p-4"
      data-testid={`plant-card-${company.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-neutral-950 text-white grid place-items-center font-mono-data text-[11px] tracking-wider shrink-0">
          {getInitials(company.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-medium text-neutral-950 truncate">
              {company.name}
            </div>
            <div className={`font-display text-lg tracking-tight shrink-0 ${pctColor}`}>
              {pct}%
            </div>
          </div>
          {company.zone && (
            <div className="font-mono-data text-[10px] uppercase tracking-[0.16em] text-neutral-500 truncate mt-0.5">
              {company.zone}
            </div>
          )}
          {company.leader && showLeader && (
            <div className="text-[11px] text-neutral-500 truncate mt-0.5">
              <span className="text-neutral-400">Líder: </span>
              {company.leader}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-1">
        {visibleModules.map((m) => {
          const implemented = Boolean(implementations[company.id]?.[m.id]);
          return (
            <StatusDot
              key={m.id}
              label={m.name}
              implemented={implemented}
              active={selectedModuleId === m.id}
              onClick={() =>
                setSelectedModuleId(selectedModuleId === m.id ? null : m.id)
              }
            />
          );
        })}
      </div>

      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-2 w-full flex items-center justify-center gap-1 text-[10px] uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-950 py-1 transition-colors font-mono-data"
          data-testid={`expand-${company.id}`}
        >
          <ChevronDown
            className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
          {expanded ? "Ver menos" : `Ver todos (${modules.length})`}
        </button>
      )}

      {selectedModule && (
        <div
          className="mt-3 border-t border-neutral-200 pt-3"
          data-testid={`plant-card-detail-${company.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                {selectedModule.category}
              </div>
              <div className="text-sm font-medium text-neutral-950 mt-0.5">
                {selectedModule.name}
              </div>
            </div>
            <span
              className={`font-mono-data text-[10px] uppercase tracking-[0.18em] px-2 py-1 ${
                selectedImpl
                  ? "bg-emerald-500 text-white"
                  : "bg-red-500 text-white"
              }`}
            >
              {selectedImpl ? "Implementado" : "Pendiente"}
            </span>
          </div>
          {(() => {
            const p = progress[company.id]?.[selectedModule.id];
            if (!p) return null;
            const pct = p.expected > 0 ? Math.round((p.achieved / p.expected) * 100) : 0;
            return (
              <div className="mt-2 text-[11px] text-neutral-600">
                Avance:{" "}
                <span className="font-medium text-neutral-950">
                  {p.achieved} de {p.expected}
                </span>{" "}
                ({pct}%)
              </div>
            );
          })()}
          {selectedCommit && (
            <div className="mt-2 text-[11px] text-neutral-600">
              Fecha compromiso:{" "}
              <span className="font-medium text-neutral-950">{selectedCommit}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MatrixCards({
  companies,
  modules,
  implementations,
  commitments = {},
  defaultCommitment = "Jun 2026",
  showLeader = true,
  progress = {},
}) {
  if (!companies.length || !modules.length) {
    return (
      <div
        className="border border-neutral-200 p-8 text-center font-mono-data text-sm uppercase tracking-[0.2em] text-neutral-500"
        data-testid="empty-state-cards"
      >
        Sin resultados
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="matrix-cards">
      {companies.map((c) => (
        <PlantCard
          key={c.id}
          company={c}
          modules={modules}
          implementations={implementations}
          commitments={commitments}
          defaultCommitment={defaultCommitment}
          showLeader={showLeader}
          progress={progress}
        />
      ))}
    </div>
  );
}
